import { html, css, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { SplitContainerItem, ComponentStore, ComponentId, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Tabs } from "@omegagrid/tabs";
import rp, { AppComponent, SqlEditorResult, SqlExecCallbackArgs, readResultStream } from "@reporting/rp-app-host";
import { CsvDescription, CsvArrayStream } from "@reporting/rp-utils-csv";
import { IOInputContainer, IOControlsContainer } from "../components";
import { IOThreadTypes, IOValidationResult } from "../types";
import { detectCsvFormat } from "../csv";
import { Thread, File as EtlFile } from '@reporting/rp-types/etl';

@customElement('rp-io-container')
export class IOContainer extends AppComponent {
	
	static styles = css`		
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
			height: 100%;
			position: relative;
			display: block;
		}
		.no-metadata {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			font-size: 15px;
			height: 100%;
		}
		.compact {
			display: flex;
			flex-direction: column;
			height: 100%;
		}
		.compact-controls {
			flex: 0 1 auto;
			min-height: 0;
			max-height: 55%;
			overflow: auto;
			border-bottom: 1px solid var(--og-border-color);
		}
		.compact-controls:only-child {
			flex: 1 1 auto;
			max-height: none;
			border-bottom: none;
		}
		.compact-content {
			flex: 1 1 auto;
			min-height: 0;
		}
	`;

	@property({type: Object})
	thread: Thread;

	@property({type: Array})
	items: SplitContainerItem[] = [{ id: 'controlsContainer', size: 300 }, { id: 'inputContainer', size: null }];

	/** Set by the host on open and resize. Mobile layout: controls above the input without a splitter, decided once at first render. */
	@property({type: Boolean})
	compact = !!rp.host?.compact;

	@state()
	private file: EtlFile;

	@state()
	private _ready = false;

	@state()
	private _compactLayout = false;

	@query('og-splitcontainer')
	splitContainer: Tabs;

	private controlsContainer: IOControlsContainer;
	private inputContainer: IOInputContainer;

	get tableColumns() { return this.file?.metadata_definition?.length > 0 ? this.file.metadata_definition : null; }
	get isImport() { return this.thread?.type === IOThreadTypes.Import; }
	get isExport() { return this.thread?.type === IOThreadTypes.Export; }
	get isDelete() { return this.thread?.type === IOThreadTypes.Delete; }

	async firstUpdated() {
		if (this.isImport) await this.loadFile();
		this._compactLayout = this.compact;
		if (this._compactLayout) {
			// the input container reads the csv descriptor of the controls, so create them in this order
			await this.componentStore.get('controlsContainer');
			await this.componentStore.get('inputContainer');
		}
		this._ready = true;
	}

	updated(props: Map<PropertyKey, unknown>) {
		if (props.has('compact')) {
			if (this.controlsContainer) this.controlsContainer.compact = this.compact;
			if (this.inputContainer) this.inputContainer.compact = this.compact;
		}
	}

	async loadFile() {
		this.file = await rp.ms('etl').get<EtlFile>(`threadFiles/${this.thread.id_thread}/primary`, { scope: 1 }) ?? null;
	}

	componentStore = new ComponentStore(async (id: ComponentId) => {
		switch (id) {
			case 'controlsContainer':
				this.controlsContainer = dom.createElement<IOControlsContainer>('rp-io-controlscontainer', {
					thread: this.thread,
					tableColumns: this.tableColumns,
					compact: this.compact,
				}) as IOControlsContainer;
				this.controlsContainer.addEventListener('io.execute', () => this._onExecute());
				this.controlsContainer.addEventListener('io.selectFile', () => this._onSelectFile());
				this.controlsContainer.addEventListener('io.changeCsvDescriptor', (e: CustomEvent<CsvDescription>) => {
					if (this.inputContainer) this.inputContainer.csvDescription = e.detail;
				});
				return this.controlsContainer;

			case 'inputContainer':
				if (!this.isImport || !this.tableColumns) return null;
				this.inputContainer = dom.createElement<IOInputContainer>('rp-io-inputcontainer', {
					thread: this.thread,
					csvDescription: this.controlsContainer.csvDescriptor,
					tableColumns: this.tableColumns,
					compact: this.compact,
				}) as IOInputContainer;
				this.inputContainer.addEventListener('io.validate', (e: CustomEvent<IOValidationResult>) => {
					this.controlsContainer.importEnabled = e.detail.valid;
					this.controlsContainer.validationMessages = e.detail.messages;
				});
				return this.inputContainer;
		}
	});

	private async _onSelectFile() {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = '.csv,.txt';
		input.onchange = () => {
			const file = input.files?.[0];
			if (file) {
				const reader = new FileReader();
				reader.onload = (e) => {
					const content = e.target?.result as string;
					const columns = this.tableColumns?.map(c => c.column) ?? [];
					this.controlsContainer.fileName = file.name;
					this.controlsContainer.updateCsvDescriptor(detectCsvFormat(content, columns, this.controlsContainer.csvDescriptor));
					this.inputContainer?.setEditorContent(content);
				};
				reader.readAsText(file);
			}
		};
		input.click();
	}

	private async _onExecute() {
		if (!this.thread || !this.controlsContainer) return;

		this.controlsContainer.showLoader();

		const paramValues = this.controlsContainer.getParameterValues();
		const csvDescriptor = this.controlsContainer.csvDescriptor;

		try {
			if (this.isImport) {
				await this._executeImport(paramValues, csvDescriptor);
			} else if (this.isExport) {
				await this._executeExport(paramValues, csvDescriptor);
			} else if (this.isDelete) {
				const confirmed = await rp.host.openMenu({
					text: msg('Are you sure you want to delete data?'),
					content: 'yes_no'
				});
				if (confirmed.id === 'Y') {
					await this._executeDelete(paramValues, csvDescriptor);
				}
			}
		} catch (err) {
			console.error('IO execution failed', err);
		}

		this.controlsContainer.hideLoader();
	}

	private async _executeImport(paramValues: Record<string, unknown>, csvDescriptor: CsvDescription) {
		const inputData = this.inputContainer?.getEditorContent();
		if (!inputData) return;

		let response: Response;
		try {
			response = await rp.ms('etl').postRaw(`io/import`, inputData, {
				customParams: paramValues,
				csvParams: csvDescriptor,
				thread: String(this.thread.id_thread),
				file: String(this.file.id_file),
				scope: 1
			}, {
				headers: { accept: 'text/csv' }
			});
			if (!response.ok) throw new Error(response.status.toString());
		} catch (error) {
			rp.host.showAlert({ type: 'danger', content: msg('Execution failed') });
			console.error('Import failed', error);
			return;
		}

		this.dispatchEvent(new CustomEvent('save', { bubbles: true, composed: true }));

		await this._readResultStream(
			response,
			`${this.thread.description ?? ''} [import]`.trimStart(),
			`io-importResult-${this.thread.id_thread}`
		);
	}

	private async _executeExport(paramValues: Record<string, unknown>, csvDescriptor: CsvDescription) {
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
		const errors = await rp.ms('etl').download(`io/export/${this.thread.id_thread}`, {
			fileName: `${this.thread.description ?? 'export'}_${timestamp}.csv`,
			csvParams: csvDescriptor,
			customParams: paramValues,
			scope: 1
		}, null, true);

		if (errors?.length > 0) {
			const div = dom.createElement<HTMLDivElement>('div');
			div.style.margin = '15px';
			div.style.userSelect = 'text';
			div.style.color = 'red';
			div.innerHTML = errors.map(e => `Line ${e.line}: ${e.error}`).join('\n');

			rp.host.bucket.openDialog({
				component: div,
				caption: msg('Result'),
				closable: true,
				width: 800,
				height: 400,
				resizable: true,
				location: 'center',
			});
		} else {
			rp.host.showAlert({ type: 'success', content: msg('Export successful.') });
		}
	}

	private async _executeDelete(paramValues: Record<string, unknown>, _csvDescriptor: CsvDescription) {
		let response: Response;
		try {
			response = await rp.ms('etl').postRaw(
				`io/delete/${this.thread.id_thread}`,
				null,
				{ customParams: paramValues, scope: 1 },
				{ headers: { accept: 'text/csv' } }
			);
			if (!response.ok) throw new Error(response.status.toString());
		} catch (error) {
			rp.host.showAlert({ type: 'danger', content: msg('Execution failed') });
			console.error('Delete failed', error);
			return;
		}

		await this._readResultStream(
			response,
			`${this.thread.description ?? ''} [delete]`.trimStart(),
			`io-deleteResult-${this.thread.id_thread}`
		);
	}

	private async _readResultStream(response: Response, title: string, tabId: string) {
		const editorResult = dom.createElement<SqlEditorResult>('rp-editor-sql-result');
		await rp.host.open(tabId, {
			title,
			color: rp.settings.get('result.tab.color') === '0' ? null : rp.settings.get('result.tab.color'),
			persistent: false,
			component: editorResult,
		});

		const reader = response.body.pipeThrough(new CsvArrayStream({
			quote: '"', delimiter: '\t', nullValue: 'NULL',
		})).getReader();

		let anyOutput = false;
		await readResultStream(reader, (args: SqlExecCallbackArgs) => {
			if (args.dataset) {
				if (!anyOutput) editorResult.clear();
				anyOutput = true;
				editorResult.addDataset(args.dataset);
			} else if (args.message) {
				anyOutput = true;
				editorResult.addMessage(args.message);
			}
		});

		if (!anyOutput) {
			editorResult.addMessage({ index: 0, content: msg('Completed with no output') });
		}
	}

	layout() {
		if (!this._compactLayout) return; // the split container lays out its own children
		this.controlsContainer?.layout();
		this.inputContainer?.layout();
	}

	render = () => {
		if (!this._ready) return nothing;
		if (this.isImport && !this.tableColumns) {
			return html`<div class="no-metadata">${msg('No input columns available for this thread.')}</div>`;
		}
		if (this._compactLayout) {
			return html`
				<div class="compact">
					<div class="compact-controls">${this.controlsContainer}</div>
					${this.inputContainer ? html`<div class="compact-content">${this.inputContainer}</div>` : nothing}
				</div>
			`;
		}
		return html`
			<og-splitcontainer
				gap="5"
				orientation="horizontal"
				.createComponent="${(id: ComponentId) => this.componentStore.get(id)}"
				.items="${this.items}"
				style="height: 100%;">
			</og-splitcontainer>
		`;
	}
}
